/**
 * React Query hooks for Question Bank
 * Provides data fetching, caching, and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../utils/api/questionBankApi';
import type { Question, WizardTemplate, QuestionBank, QuestionFilters } from '../utils/api/questionBankApi';

// ==================== QUERY KEYS ====================

export const questionBankKeys = {
  all: ['questionBank'] as const,
  bank: (firmId: string) => [...questionBankKeys.all, firmId] as const,
  questions: (firmId: string) => [...questionBankKeys.bank(firmId), 'questions'] as const,
  question: (firmId: string, questionId: string) => [...questionBankKeys.questions(firmId), questionId] as const,
  questionsByCategory: (firmId: string, category: string) => 
    [...questionBankKeys.questions(firmId), 'category', category] as const,
  wizards: (firmId: string) => [...questionBankKeys.bank(firmId), 'wizards'] as const,
  wizard: (firmId: string, wizardId: string) => [...questionBankKeys.wizards(firmId), wizardId] as const,
  wizardsByCategory: (firmId: string, category: string) => 
    [...questionBankKeys.wizards(firmId), 'category', category] as const,
  categories: (firmId: string) => [...questionBankKeys.bank(firmId), 'categories'] as const,
};

// ==================== QUESTION BANK ====================

/**
 * Hook to get entire question bank
 */
export function useQuestionBank(firmId: string) {
  return useQuery({
    queryKey: questionBankKeys.bank(firmId),
    queryFn: () => api.getQuestionBank(firmId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!firmId,
  });
}

/**
 * Hook to populate question bank (initial setup)
 */
export function usePopulateQuestionBank() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (firmId: string) => api.populateQuestionBank(firmId),
    onSuccess: (data, firmId) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Question bank populated successfully!', {
        description: `${Object.keys(data.questions).length} questions loaded`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to populate question bank', {
        description: error.message,
      });
    },
  });
}

// ==================== QUESTIONS ====================

/**
 * Hook to get all questions
 */
export function useQuestions(firmId: string) {
  return useQuery({
    queryKey: questionBankKeys.questions(firmId),
    queryFn: () => api.getAllQuestions(firmId),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId,
  });
}

/**
 * Hook to get questions by category
 */
export function useQuestionsByCategory(firmId: string, category: string) {
  return useQuery({
    queryKey: questionBankKeys.questionsByCategory(firmId, category),
    queryFn: () => api.getQuestionsByCategory(firmId, category),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId && !!category,
  });
}

/**
 * Hook to get a single question
 */
export function useQuestion(firmId: string, questionId: string) {
  return useQuery({
    queryKey: questionBankKeys.question(firmId, questionId),
    queryFn: () => api.getQuestion(firmId, questionId),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId && !!questionId,
  });
}

/**
 * Hook to create a new question
 */
export function useCreateQuestion(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (question: Partial<Question>) => api.createQuestion(firmId, question),
    onSuccess: (newQuestion) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.questions(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Question created!', {
        description: `"${newQuestion.text}" has been added to the question bank`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to create question', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update a question
 */
export function useUpdateQuestion(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ questionId, updates }: { questionId: string; updates: Partial<Question> }) =>
      api.updateQuestion(firmId, questionId, updates),
    onSuccess: (updatedQuestion, variables) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.questions(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.question(firmId, variables.questionId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Question updated!', {
        description: `"${updatedQuestion.text}" has been updated`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to update question', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to delete a question
 */
export function useDeleteQuestion(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionId: string) => api.deleteQuestion(firmId, questionId),
    onSuccess: (_, questionId) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.questions(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Question deleted', {
        description: 'The question has been removed from the bank',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to delete question', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to duplicate a question
 */
export function useDuplicateQuestion(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionId: string) => api.duplicateQuestion(firmId, questionId),
    onSuccess: (duplicatedQuestion) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.questions(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Question duplicated!', {
        description: `Created editable copy: "${duplicatedQuestion.text}"`,
      });
      return duplicatedQuestion;
    },
    onError: (error: Error) => {
      toast.error('Failed to duplicate question', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to bulk delete questions
 */
export function useBulkDeleteQuestions(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionIds: string[]) => api.bulkDeleteQuestions(firmId, questionIds),
    onSuccess: (_, questionIds) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.questions(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success(`${questionIds.length} questions deleted`, {
        description: 'The selected questions have been removed',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to delete questions', {
        description: error.message,
      });
    },
  });
}

// ==================== WIZARDS ====================

/**
 * Hook to get all wizards
 */
export function useWizards(firmId: string) {
  return useQuery({
    queryKey: questionBankKeys.wizards(firmId),
    queryFn: () => api.getAllWizards(firmId),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId,
  });
}

/**
 * Hook to get wizards by category
 */
export function useWizardsByCategory(firmId: string, category: string) {
  return useQuery({
    queryKey: questionBankKeys.wizardsByCategory(firmId, category),
    queryFn: () => api.getWizardsByCategory(firmId, category),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId && !!category,
  });
}

/**
 * Hook to get a single wizard
 */
export function useWizard(firmId: string, wizardId: string) {
  return useQuery({
    queryKey: questionBankKeys.wizard(firmId, wizardId),
    queryFn: () => api.getWizard(firmId, wizardId),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId && !!wizardId,
  });
}

/**
 * Hook to create a new wizard
 */
export function useCreateWizard(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (wizard: Partial<WizardTemplate>) => api.createWizard(firmId, wizard),
    onSuccess: (newWizard) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.wizards(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Wizard created!', {
        description: `"${newWizard.name}" has been added to the wizard library`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to create wizard', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update a wizard
 */
export function useUpdateWizard(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ wizardId, updates }: { wizardId: string; updates: Partial<WizardTemplate> }) =>
      api.updateWizard(firmId, wizardId, updates),
    onSuccess: (updatedWizard, variables) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.wizards(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.wizard(firmId, variables.wizardId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Wizard updated!', {
        description: `"${updatedWizard.name}" has been updated`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to update wizard', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to delete a wizard
 */
export function useDeleteWizard(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (wizardId: string) => api.deleteWizard(firmId, wizardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.wizards(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Wizard deleted', {
        description: 'The wizard has been removed',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to delete wizard', {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to check auto-launch wizards
 */
export function useAutoLaunchWizards(firmId: string, category: string, configItemId: string) {
  return useQuery({
    queryKey: [...questionBankKeys.wizardsByCategory(firmId, category), 'auto-launch', configItemId],
    queryFn: () => api.checkAutoLaunchWizards(firmId, category, configItemId),
    enabled: !!firmId && !!category && !!configItemId,
  });
}

// ==================== CATEGORIES ====================

/**
 * Hook to get categories
 */
export function useCategories(firmId: string) {
  return useQuery({
    queryKey: questionBankKeys.categories(firmId),
    queryFn: () => api.getCategories(firmId),
    staleTime: 5 * 60 * 1000,
    enabled: !!firmId,
  });
}

/**
 * Hook to update category mapping
 */
export function useUpdateCategoryMapping(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ category, mapping }: {
      category: string;
      mapping: {
        defaultQuestionIds?: string[];
        recommendedQuestionIds?: string[];
        wizardTemplateIds?: string[];
      };
    }) => api.updateCategoryMapping(firmId, category, mapping),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: questionBankKeys.categories(firmId) });
      queryClient.invalidateQueries({ queryKey: questionBankKeys.bank(firmId) });
      toast.success('Category updated!', {
        description: `"${variables.category}" mapping has been updated`,
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to update category', {
        description: error.message,
      });
    },
  });
}

// ==================== SEARCH & FILTER ====================

/**
 * Hook for searching and filtering questions
 */
export function useQuestionSearch(firmId: string, searchQuery: string, filters?: QuestionFilters) {
  const { data: allQuestions, ...rest } = useQuestions(firmId);

  const filteredQuestions = React.useMemo(() => {
    if (!allQuestions) return [];

    let result = allQuestions;

    // Apply search
    if (searchQuery) {
      result = api.searchQuestions(result, searchQuery);
    }

    // Apply filters
    if (filters) {
      result = api.filterQuestions(result, filters);
    }

    return result;
  }, [allQuestions, searchQuery, filters]);

  return {
    ...rest,
    data: filteredQuestions,
    allQuestions,
  };
}

// Import React for useMemo
import React from 'react';